import { useState, useEffect, useCallback } from "react";

interface UseScrollHeaderReturn {
  isScrolled: boolean;
  scrollDirection: "up" | "down";
  scrollY: number;
}

export function useScrollHeader(
  threshold: number = 100,
): UseScrollHeaderReturn {
  const [isScrolled, setIsScrolled] = useState(false);
  const [scrollDirection, setScrollDirection] = useState<"up" | "down">("up");
  const [scrollY, setScrollY] = useState(0);
  const [lastScrollY, setLastScrollY] = useState(0);

  const updateScrollState = useCallback(() => {
    const currentScrollY = window.scrollY;

    // Only update if there's a meaningful change (avoid micro-scrolls)
    if (Math.abs(currentScrollY - lastScrollY) < 5) return;

    setScrollY(currentScrollY);
    setIsScrolled(currentScrollY > threshold);

    // Determine scroll direction
    if (currentScrollY > lastScrollY && currentScrollY > threshold) {
      setScrollDirection("down");
    } else if (currentScrollY < lastScrollY) {
      setScrollDirection("up");
    }

    setLastScrollY(currentScrollY);
  }, [lastScrollY, threshold]);

  useEffect(() => {
    const handleScroll = () => {
      requestAnimationFrame(updateScrollState);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    // Set initial state
    updateScrollState();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [updateScrollState]);

  return {
    isScrolled,
    scrollDirection,
    scrollY,
  };
}
